import { useCallback, useEffect, useMemo, useState } from "react";
import { OverlayTrigger } from "react-bootstrap";
import Popover from "react-bootstrap/Popover";

import "rc-input-number/assets/index.css";
import classNames from "classnames";
import InputNumber from "rc-input-number";
import { twMerge } from "tailwind-merge";
import { useDebounce, useUpdateEffect } from "usehooks-ts";

import { faCaretDown } from "@fortawesome/free-solid-svg-icons/faCaretDown";

import FontAwesomeIcon from "components/FontAwesomeIcon";

interface Props {
  label: string;
  max: number;
  min: number;
  step?: number;
  filterKey: string;
  onValueChange: (_min: number, _max: number, _key: any) => void;
  filters: any;
  narrow?: boolean;
  showInput?: boolean;
  containerClassName?: string;
  valueFormating?: (_value: number, _isLast?: boolean) => void;
  alwaysShowValues?: boolean;
}

export default function RangeSelector({
  label,
  max = 5,
  min = 0,
  step = 1,
  filterKey,
  onValueChange,
  filters,
  narrow,
  showInput,
  containerClassName,
  valueFormating,
  alwaysShowValues,
}: Props) {
  const [open, setOpen] = useState(false);
  const [maxValue, setMaxValue] = useState(max);
  const [minValue, setMinValue] = useState(min);

  const debouncedMinValue = useDebounce<number>(minValue, 200);
  const debouncedMaxValue = useDebounce<number>(maxValue, 200);

  const getPercent = useCallback(
    (value) => Math.round(((value - min) / (max - min)) * 100),
    [min, max]
  );

  const onMinChange = useCallback(
    (value: number | null) => {
      if (value !== null) {
        setMinValue(Math.min(value, maxValue));
        // onValueChange(value, maxValue, filterKey); // TODO: fix issue with debounce in smartscore screener
      }
    },
    [maxValue]
  );

  const onMaxChange = useCallback(
    (value: number | null) => {
      if (value) {
        setMaxValue(Math.max(value, minValue));
        // onValueChange(minValue, value, filterKey); // TODO: fix issue with debounce in smartscore screener
      }
    },
    [minValue]
  );

  useUpdateEffect(() => {
    onValueChange(debouncedMinValue, debouncedMaxValue, filterKey);
  }, [debouncedMaxValue, debouncedMinValue, filterKey]);

  const [barLeft, barWidth] = useMemo(() => {
    const minPercent = getPercent(minValue);
    const maxPercent = getPercent(maxValue);

    return [`${minPercent}%`, `${maxPercent - minPercent}%`];
  }, [getPercent, minValue, maxValue]);

  useEffect(() => {
    if (!filters[filterKey]) {
      setMaxValue(max);
      setMinValue(min);
    }
  }, [filterKey, filters, max, min]);

  return (
    <div className={twMerge("w-[200px]", containerClassName)}>
      <OverlayTrigger
        placement="bottom"
        trigger={"click"}
        rootClose
        show={open}
        onExit={() => {
          setOpen(false);
        }}
        onToggle={(data) => {
          if (!data) {
            setOpen(data);
          }
        }}
        overlay={
          <Popover className="overflow-hidden select-none shadow-xl">
            <Popover.Body className="!p-0 w-[250px]">
              <div className="p-[10px]">
                {label} ({valueFormating?.(minValue) || minValue} to{" "}
                {valueFormating?.(maxValue, true) || maxValue})
                <div
                  className={twMerge(
                    "relative items-center justify-center mt-[15px]",
                    showInput ? "h-[80px]" : "h-[30px]"
                  )}
                >
                  {showInput && (
                    <div className="flex w-full items-center justify-center mb-[15px] space-x-[30px]">
                      <InputNumber
                        max={maxValue}
                        min={min}
                        value={minValue}
                        step={step}
                        onChange={onMinChange}
                      />
                      <InputNumber
                        max={max}
                        min={minValue}
                        value={maxValue}
                        onChange={onMaxChange}
                        step={step}
                      />
                    </div>
                  )}
                  <div className="flex w-full items-center justify-center z-1">
                    <input
                      type="range"
                      max={max}
                      min={min}
                      value={minValue}
                      step={step}
                      onChange={(e) => onMinChange(parseFloat(e.target.value))}
                      className={classNames(
                        "!h-0 p-0 w-full bg-red-100 !border-0 absolute z-[3] pointer-events-none thumb",
                        minValue === 5 && "z-[5]"
                      )}
                    />
                    <input
                      type="range"
                      max={max}
                      min={min}
                      value={maxValue}
                      onChange={(e) => onMaxChange(parseFloat(e.target.value))}
                      step={step}
                      className="!h-0 p-0 w-full bg-red-100 !border-0 absolute z-[3] pointer-events-none thumb thumb-right"
                    />
                  </div>
                  <div className="absolute w-full z-0">
                    <div
                      style={{ left: barLeft, width: barWidth }}
                      className="bg-smart-teal h-[5px] absolute rounded"
                    ></div>
                    <div className="w-[100%] h-[5px] bg-inactive-gray rounded"></div>
                    <div className="flex justify-between mt-[5px]">
                      <span>{min}</span>
                      <span>{valueFormating?.(max) || max}</span>
                    </div>
                  </div>
                </div>
              </div>
            </Popover.Body>
          </Popover>
        }
      >
        <div
          className="p-[10px] text-[12px] shadow-md rounded flex items-center justify-between"
          onClick={() => setOpen(!open)}
        >
          <label className="truncate">
            {label}
            {narrow && <br />}
            {(minValue != 0 || maxValue != 5 || alwaysShowValues) && (
              <span className={twMerge(!narrow && "ml-[5px]")}>
                ({valueFormating?.(minValue) || minValue} to{" "}
                {valueFormating?.(maxValue, true) || maxValue})
              </span>
            )}
          </label>
          <FontAwesomeIcon icon={faCaretDown} className="ml-[10px]" />
        </div>
      </OverlayTrigger>
    </div>
  );
}
